import React from "react";
import { Box } from "@mui/material";
import { styled, SxProps, Theme } from "@mui/material/styles";

interface SearchBoxProps {
  title: string;
  id: string;
  placeholder: string;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  sxLabel?: SxProps<Theme>;
  sxTextField?: SxProps<Theme>;
  readOnly?: boolean;
  value?: string;
  fieldWidth?: string;
  onChangeText?: (value: string) => void;
  maxLength?: number;
  type?: string;
  disabled?: boolean;
}

const SearchFieldLabel = styled("span")({
  fontWeight: "bold",
  backgroundColor: "var(--primary-500)",
  padding: "10px",
  display: "inline-block",
  whiteSpace: "nowrap",
  minWidth: "180px",
  flexShrink: 0,
  flexGrow: 1,
  textAlign: "center",
});

const StyledInput = styled("input")(() => ({
  width: "100%",
  border: "1px solid var(--gray-100)",
  height: "44px",
  marginTop: "0px",
  padding: "10px",
  // Remove number input spinners (Chrome, Safari, Edge, Opera)
  "&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  // Remove number input spinners (Firefox)
  "&[type=number]": {
    MozAppearance: "textfield",
  },
}));

/**
 * A component that renders a search box with a label and an input field.
 * @param {object} props - The properties object.
 * @param {string} props.title - The title to be displayed as the label.
 * @param {string} props.id - The id for the input field.
 * @param {string} props.placeholder - The placeholder text for the input field.
 * @param {SxProps<Theme>} [props.sx] - Optional styling properties to be applied to the container.
 * @param {React.ReactNode} [props.children] - Optional children to be rendered instead of the default input field.
 * @returns {Element} The rendered search box component.
 */
function SearchBox({
  title,
  id,
  placeholder,
  sx,
  children,
  sxLabel = {},
  sxTextField,
  readOnly = false,
  value,
  fieldWidth,
  onChangeText,
  maxLength,
  type = "text",
  disabled = false,
}: SearchBoxProps) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", columnGap: "10px", minWidth: "450px", ...sx }}
    >
      <SearchFieldLabel sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, ...sxLabel }}>
        {title}
      </SearchFieldLabel>

      {children ? (
        <Box width={fieldWidth}>{children}</Box>
      ) : (
        <StyledInput
          maxLength={maxLength}
          sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, ...sxTextField }}
          type={type}
          id={id}
          placeholder={placeholder}
          readOnly={readOnly}
          value={value}
          onChange={(e) => onChangeText && onChangeText(e.target.value)}
          disabled={disabled}
        />
      )}
    </Box>
  );
}

export default SearchBox;
